import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { FixedAssetValueService } from '../../services/fixedAssetValue.service';
import { FixedAssetCategoryService } from '../../services/fixedAssetCategory.service';
import { AccountService } from '../../services/account.service';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import { postingAccounts } from '../../utils/accountHierarchy';
import { Account } from '../../interfaces/accounting.interface';
import SearchableSelect from '../common/SearchableSelect';
import { BillLineCandidate } from '../../interfaces/fixedAsset/fixedAssetValue.interface';
import { FixedAssetCategory } from '../../interfaces/fixedAsset/cwip.interface';

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

interface Props {
  open: boolean;
  institutionId: string;
  billId: string;
  onClose: () => void;
}

const RegisterFromBillLineDialog: React.FC<Props> = ({ open, institutionId, billId, onClose }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [lines, setLines] = useState<BillLineCandidate[]>([]);
  const [categories, setCategories] = useState<FixedAssetCategory[]>([]);
  const [lineId, setLineId] = useState('');
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [salvage, setSalvage] = useState('0');
  const [lifeMonths, setLifeMonths] = useState('');
  const [method, setMethod] = useState<'STRAIGHT_LINE' | 'REDUCING_BALANCE'>('STRAIGHT_LINE');
  const [capitalisedTax, setCapitalisedTax] = useState('');
  const [taxAccountId, setTaxAccountId] = useState('');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    FixedAssetValueService.billLines(billId).then(ls => {
      setLines(ls);
      const acct = ls[0]?.nonRecoverableTaxAccountId;
      if (acct) setTaxAccountId(acct);
    })
      .catch(e => toast.showApiError(e, 'Failed to load bill lines'));
    AccountService.getLookup().then(r => setAccounts(r ?? [])).catch(() => setAccounts([]));
    FixedAssetCategoryService.list(institutionId).then(setCategories).catch(() => setCategories([]));
  }, [open, billId, institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = lines.find(l => l.journalLineId === lineId);
  const taxAvailable = lines[0]?.nonRecoverableTaxAvailable ?? 0;
  const taxAccountOptions = postingAccounts(accounts).filter(a => a.accountType === 'EXPENSE');
  const taxAmount = parseFloat(capitalisedTax) || 0;
  const taxInvalid = taxAmount < 0 || taxAmount > taxAvailable || (taxAmount > 0 && !taxAccountId);

  const submit = async () => {
    try {
      setSaving(true);
      const asset = await FixedAssetValueService.fromBillLine({
        institutionId, supplierBillId: billId, journalLineId: lineId, name: name.trim(),
        categoryId: categoryId || undefined, salvageValue: parseFloat(salvage) || 0,
        usefulLifeMonths: lifeMonths ? parseInt(lifeMonths, 10) : undefined, depreciationMethod: method,
        capitalisedTax: taxAmount > 0 ? taxAmount : undefined, taxExpenseAccountId: taxAmount > 0 ? taxAccountId : undefined,
      });
      toast.showSuccess('Asset registered from bill line');
      onClose();
      navigate(`/fixed-assets/${asset.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to register asset');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog data-help-page="fixedAssets.billLine.page" open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Register as fixed asset</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <Alert severity="info">
            The bill's journal is the acquisition journal; no new posting is made. Cost excludes recoverable VAT.
          </Alert>
          <SearchableSelect<BillLineCandidate> data-help="fixedAssets.billLine.field.line"
            options={lines}
            value={lineId}
            onChange={(id, opt) => { setLineId(id); if (opt && !name) setName(opt.description ?? opt.accountName); }}
            getOptionId={l => l.journalLineId}
            getOptionLabel={l => `${l.accountCode} ${l.accountName} — ${money(l.amount)}${l.eligible ? '' : ' (not eligible)'}`}
            getOptionDisabled={l => !l.eligible}
            label="Bill line"
          />
          {selected && !selected.eligible && (
            <Typography variant="caption" color="error">{selected.ineligibleReason}</Typography>
          )}
          <TextField data-help="fixedAssets.billLine.field.name" label="Asset name" value={name} onChange={e => setName(e.target.value)} required />
          <SearchableSelect<FixedAssetCategory> data-help="fixedAssets.billLine.field.category"
            options={categories} value={categoryId} onChange={id => setCategoryId(id)}
            getOptionLabel={c => c.name} label="Category (optional)"
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField data-help="fixedAssets.billLine.field.salvage" sx={{ flex: '1 1 160px', maxWidth: 260 }} label="Salvage value" type="number"
              value={salvage} onChange={e => setSalvage(e.target.value)} />
            <TextField data-help="fixedAssets.billLine.field.life" sx={{ flex: '1 1 160px', maxWidth: 260 }} label="Useful life (months)" type="number"
              value={lifeMonths} onChange={e => setLifeMonths(e.target.value)} />
            <TextField data-help="fixedAssets.billLine.field.method" sx={{ flex: '1 1 160px', maxWidth: 260 }} select label="Method" value={method}
              onChange={e => setMethod(e.target.value as 'STRAIGHT_LINE' | 'REDUCING_BALANCE')}>
              <MenuItem value="STRAIGHT_LINE">Straight line</MenuItem>
              <MenuItem value="REDUCING_BALANCE">Reducing balance</MenuItem>
            </TextField>
          </Box>
          {taxAvailable > 0 && (
            <>
              <Alert severity="warning">
                This bill booked {money(taxAvailable)} of non-recoverable VAT/SD to expense. Under IAS 16.16(a) the part that
                belongs to this asset is part of its cost: enter it to add it to the cost and move it out of expense.
              </Alert>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <TextField data-help="fixedAssets.billLine.field.tax" sx={{ flex: '1 1 180px', maxWidth: 260 }} label="Non-recoverable tax to capitalise" type="number"
                  value={capitalisedTax} error={taxInvalid} onChange={e => setCapitalisedTax(e.target.value)} />
                <Box sx={{ flex: '1 1 260px', maxWidth: 420 }}>
                  <AccountAutocomplete data-help="fixedAssets.billLine.field.taxAccount" accounts={taxAccountOptions} value={taxAccountId}
                    onChange={setTaxAccountId} label="Tax expense account the bill debited" placeholder="Select" />
                </Box>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button data-help="fixedAssets.billLine.cancel" onClick={onClose}>Cancel</Button>
        <Button data-help="fixedAssets.billLine.save" variant="contained" onClick={submit}
          disabled={saving || !selected?.eligible || !name.trim() || taxInvalid}>Register</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegisterFromBillLineDialog;
